/* ============================================================
   AdminPages.jsx — Varor & lager, Kioskschema, Försäljning,
   Inställningar och Kioskguiden (plus the product/group forms).
   ============================================================ */

import { useRef, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { Modal, Stepper, Stat, EmojiPick, Thumb, PaymentTag, SaleItems, askConfirm } from '../components/ui.jsx';
import { PageHead, PeriodToggle, Segmented } from '../components/admin-shell.jsx';
import { useStore, actions, shortagesFor, dismissError } from '../store/store.js';
import { kr, isoWeek, isoDay, DAY_NAMES, rangeFor, shiftPeriod, periodLabel, stockFor, priceFor, discountFor, costFor, isUntracked, salesByDay } from '../store/selectors.js';

/* ---------------- VAROR & LAGER ---------------- */
export function Inventory() {
  const state = useStore();
  const [edit, setEdit] = useState(null);    // product being edited, or 'new'
  const [gEdit, setGEdit] = useState(null);  // group being edited, or 'new'

  return (
    <div className="scroll" style={{ height: '100%' }}>
      <PageHead sticky title="Varor & lager" sub="Skapa grupper och varor, sätt pris, inköpspris och antal i lager."
        right={<div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" onClick={() => setGEdit('new')}><Icon name="plus" size={18} /> Ny grupp</button>
          <button className="btn btn-primary" onClick={() => setEdit('new')}><Icon name="plus" size={19} /> Ny vara</button>
        </div>} />
      <div className="admin-body">
        {state.groups.length === 0 && (
          <div className="card center" style={{ flexDirection: 'column', gap: 12, padding: '48px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 46 }}>🧺</div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 21 }}>Inget sortiment än</div>
            <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14.5, lineHeight: 1.55, maxWidth: 420 }}>
              Börja med en grupp – till exempel <b>Varm mat</b> eller <b>Dryck</b> – och lägg sedan varorna i den.
              Säljer ni korv och burgare? Skapa lagervarorna under <b>Inventering</b> först, så kan varorna
              dra från dem automatiskt.
            </p>
            <button className="btn btn-primary" style={{ marginTop: 6 }} onClick={() => setGEdit('new')}>
              <Icon name="plus" size={19} /> Skapa första gruppen
            </button>
          </div>
        )}
        {state.groups.map(g => {
          const items = state.products.filter(p => p.groupId === g.id);
          return (
            <div key={g.id} className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
              <div className="group-head" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, padding: '16px 22px', background: g.color + '12' }}>
                <div className="center" style={{ width: 38, height: 38, borderRadius: 11, background: g.color + '22', fontSize: 20, flexShrink: 0 }}>{g.emoji}</div>
                {/* Minst 160px för namnet — på en mobil hoppar knapparna ned en rad i stället. */}
                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 9px' }}>
                    <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18 }}>{g.name}</span>
                    {g.untracked && <span className="pill tag-neutral" style={{ fontSize: 11 }} title="Varorna har bara ett pris — inget antal eller inköpspris">utan lagersaldo</span>}
                    {g.discount > 0 && <span className="pill tag-warn" style={{ fontSize: 11 }} title="Räknas på ordinarie pris och trumfar extrapriser">−{g.discount} % på allt</span>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{items.length} varor</div>
                </div>
                {g.discount > 0 && (
                  <button className="btn btn-sm btn-soft" title="Ta bort rabatten — ordinarie pris gäller igen" onClick={() => actions.updateGroup(g.id, { discount: 0 })}>
                    <Icon name="x" size={14} /> Ta bort rabatt
                  </button>
                )}
                <button className="btn btn-icon btn-ghost" onClick={() => setGEdit(g)}><Icon name="edit" size={17} /></button>
                <button className="btn btn-icon btn-ghost" onClick={() => {
                  askConfirm(`Gruppen „${g.name}” och alla dess varor tas bort permanent.`,
                    { title: 'Ta bort grupp?', danger: true, confirmLabel: 'Ta bort' })
                    .then(ok => ok && actions.deleteGroup(g.id));
                }}><Icon name="trash" size={17} /></button>
              </div>

              <div className="varor-row varor-head" style={{ padding: '10px 22px', fontSize: 12, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', borderBottom: '1.5px solid var(--line-soft)' }}>
                <span>Vara</span><span>Pris</span><span className="col-sm-hide">Inköp</span><span className="col-sm-hide">Marginal</span><span>I lager</span><span />
              </div>
              {items.length === 0 && <div style={{ padding: '16px 22px', color: 'var(--muted)', fontSize: 14, fontWeight: 600 }}>Inga varor än – tryck "Ny vara".</div>}
              {items.map(p => {
                const untracked = !!g.untracked || !!p.untracked;
                const price = priceFor(p, state);
                const cost = costFor(state, p);
                const disc = discountFor(state, p);
                const onSale = price !== p.price;
                const m = price - cost;
                const mp = price > 0 ? Math.round(m / price * 100) : 0;
                const st = stockFor(state, p);
                const low = !untracked && st > 0 && st <= state.settings.lowStock;
                const out = !untracked && st <= 0;
                const fill = p.restockQty || 12;
                return (
                  <div key={p.id} className="varor-row" style={{ padding: '12px 22px', borderBottom: '1px solid var(--line-soft)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      <Thumb p={p} size={40} radius={11} />
                      <span style={{ fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                      {p.meal && <span className="pill tag-warn" style={{ fontSize: 10.5, flexShrink: 0 }} title="Varje sålt räknas av från grillen">🍔 mål</span>}
                      {p.freeForLeaders && <span className="pill tag-neutral" style={{ fontSize: 10.5, flexShrink: 0 }} title="Gratis till ledare under Domare & ledare i kassan">☕ ledare</span>}
                      {p.freeForReferees && <span className="pill tag-neutral" style={{ fontSize: 10.5, flexShrink: 0 }} title="Gratis till domare under Domare & ledare i kassan">🧑‍⚖️ domare</span>}
                      {p.choiceGroupId && <span className="pill tag-neutral" style={{ fontSize: 10.5, flexShrink: 0 }} title="Kunden väljer en vara ur gruppen — ingår i priset">+ {(state.groups.find(x => x.id === p.choiceGroupId) || {}).name || 'tillval'}</span>}
                    </div>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16, color: onSale ? 'var(--orange-deep)' : 'var(--ink)' }}>{price} kr</span>
                      {onSale && <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: 'var(--muted)' }}><s>{p.price} kr</s> · {disc > 0 ? `−${disc} % rabatt` : 'extrapris'}</span>}
                    </span>
                    <span className="col-sm-hide" style={{ color: 'var(--ink-soft)', fontWeight: 600 }}>{untracked ? '—' : `${cost} kr`}</span>
                    <span className="col-sm-hide">{untracked
                      ? <span style={{ color: 'var(--muted)', fontWeight: 600 }}>—</span>
                      : <span className="pill tag-good" style={{ fontSize: 12.5 }}>+{m} kr · {mp}%</span>}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {untracked ? (
                        <span className="pill tag-neutral" style={{ fontSize: 10.5 }} title="Gruppen saknar lagersaldo — varan säljs utan antal">utan antal</span>
                      ) : (<>
                        <span style={{ fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16, minWidth: 26, color: out ? 'var(--bad)' : low ? 'var(--orange-deep)' : 'var(--ink)' }}>{st}</span>
                        {out && <span className="pill tag-bad" style={{ fontSize: 11 }}>SLUT</span>}
                        {low && <span className="pill tag-warn" style={{ fontSize: 11 }}>lågt</span>}
                        {p.recipe
                          ? <span className="pill tag-neutral" style={{ fontSize: 10.5 }} title="Beräknas från lagervaror (Inventering)">från lagervaror</span>
                          : <button className="btn btn-sm btn-soft" style={{ padding: '6px 9px' }} onClick={() => actions.restock(p.id, fill)} title={`Fyll på ${fill} — ändra antalet på varan`}><Icon name="plus" size={14} />{fill}</button>}
                      </>)}
                    </div>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn btn-icon btn-ghost" onClick={() => setEdit(p)}><Icon name="edit" size={16} /></button>
                      <button className="btn btn-icon btn-ghost" onClick={() => {
                        askConfirm(`„${p.name}” tas bort ur sortimentet.`,
                          { title: 'Ta bort vara?', danger: true, confirmLabel: 'Ta bort' })
                          .then(ok => ok && actions.deleteProduct(p.id));
                      }}><Icon name="trash" size={16} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {edit && <ProductForm product={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
      {gEdit && <GroupForm group={gEdit === 'new' ? null : gEdit} onClose={() => setGEdit(null)} />}
    </div>
  );
}

/* ---------------- PRODUCT FORM ---------------- */
const EMPTY_PRODUCT = {
  name: '', price: '', cost: '', stock: '', salePrice: '', restockQty: '', emoji: '🍽️', image: null, recipe: null,
  untracked: false, meal: false, freeForLeaders: false, freeForReferees: false, choiceGroupId: null,
};

function ProductForm({ product, onClose }) {
  const state = useStore();
  const [f, setF] = useState(product
    ? { ...product, salePrice: product.salePrice ?? '', restockQty: product.restockQty ?? '' }
    : { ...EMPTY_PRODUCT, groupId: state.groups[0]?.id });
  const [busy, setBusy] = useState(false);
  const [multi, setMulti] = useState(false);       // skapa fler varor i rad
  const [justSaved, setJustSaved] = useState(''); // namnet på senast sparade
  const nameRef = useRef(null);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const valid = f.name.trim() && f.groupId && f.price !== '';
  const recipe = f.recipe || [];
  const hasRecipe = recipe.length > 0;
  const group = state.groups.find(g => g.id === f.groupId);
  const groupUntracked = !!(group && group.untracked);
  const untracked = groupUntracked || !!f.untracked;

  /* Inköpspriset för receptvaror härleds från lagervarorna. */
  const recipeCost = recipe.reduce((a, [iid, q]) => {
    const ing = state.ingredients.find(x => x.id === iid);
    return a + (ing ? (ing.cost || 0) * q : 0);
  }, 0);
  const effPrice = f.salePrice !== '' ? +f.salePrice || 0 : +f.price || 0;
  const cost = untracked ? 0 : hasRecipe ? recipeCost : +f.cost || 0;
  const margin = effPrice - cost;

  async function save() {
    if (busy) return;
    setBusy(true);
    const patch = {
      name: f.name.trim(), groupId: f.groupId, price: +f.price || 0,
      cost: untracked ? 0 : +f.cost || 0,
      stock: untracked ? 0 : +f.stock || 0,
      salePrice: f.salePrice === '' ? null : Math.max(0, +f.salePrice || 0),
      restockQty: f.restockQty === '' ? null : Math.max(0, +f.restockQty || 0),
      emoji: f.emoji, image: f.image || null,
      recipe: hasRecipe && !untracked ? recipe : null, meal: !!f.meal,
      untracked: !!f.untracked,
      freeForLeaders: !!f.freeForLeaders, freeForReferees: !!f.freeForReferees,
      choiceGroupId: f.choiceGroupId || null,
    };
    try {
      if (product) {
        await actions.updateProduct(product.id, patch);
        onClose();
        return;
      }
      await actions.addProduct(patch);
      if (!multi) {
        onClose();
        return;
      }
      /* Fortsätt med nästa vara: gruppen (och symbolen) behålls, resten töms. */
      setJustSaved(patch.name);
      setF({ ...EMPTY_PRODUCT, groupId: f.groupId, emoji: f.emoji });
      setBusy(false);
      nameRef.current && nameRef.current.focus();
      setTimeout(() => setJustSaved(''), 2500);
    } catch {
      setBusy(false);
    }
  }

  return (
    <Modal title={product ? 'Ändra vara' : 'Ny vara'} onClose={onClose} wide
      footer={<>
        {!product && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, fontWeight: 700, color: 'var(--ink-soft)', cursor: 'pointer' }}>
            <input type="checkbox" checked={multi} onChange={e => setMulti(e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--green)' }} />
            Skapa fler varor i rad
          </label>
        )}
        <div style={{ flex: 1 }} />
        {justSaved && <span className="pill tag-good" style={{ fontSize: 13 }}>✓ „{justSaved}” sparad</span>}
        <button className="btn btn-ghost" onClick={onClose}>{multi && !product ? 'Klar' : 'Avbryt'}</button>
        <button className="btn btn-primary" disabled={!valid || busy} onClick={save}>
          <Icon name="check" size={19} /> {multi && !product ? 'Spara & nästa' : 'Spara'}
        </button>
      </>}>
      <div className="grid-form">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="field"><label>Namn</label>
            <input className="input" ref={nameRef} value={f.name} autoFocus onChange={e => set('name', e.target.value)} placeholder="T.ex. Korv med bröd" /></div>
          <div className="field"><label>Grupp</label>
            <select className="select" value={f.groupId} onChange={e => set('groupId', e.target.value)}>
              {state.groups.map(g => <option key={g.id} value={g.id}>{g.emoji} {g.name}{g.untracked ? ' (utan lagersaldo)' : ''}</option>)}
            </select></div>
          <div className="form-cols" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="field"><label style={{ whiteSpace: 'nowrap' }}>Pris (kr)</label>
              <input className="input" type="number" inputMode="numeric" value={f.price} onChange={e => set('price', e.target.value)} placeholder="25" /></div>
            <div className="field"><label style={{ whiteSpace: 'nowrap' }}>Extrapris (kr) — tillfälligt</label>
              <input className="input" type="number" inputMode="numeric" value={f.salePrice} onChange={e => set('salePrice', e.target.value)} placeholder="Tomt = ordinarie" /></div>
          </div>
          {f.salePrice !== '' && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: '#fbeed8', borderRadius: 12, padding: '10px 14px', fontSize: 13.5, fontWeight: 700, color: 'var(--orange-deep)' }}>
              <Icon name="warn" size={17} />
              Kassan tar {+f.salePrice || 0} kr istället för {+f.price || 0} kr tills extrapriset tas bort.
            </div>
          )}
          {!groupUntracked && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: f.untracked ? 'var(--green-deep)' : 'var(--ink-soft)' }}>
              <input type="checkbox" checked={!!f.untracked} onChange={e => set('untracked', e.target.checked)}
                style={{ width: 18, height: 18, accentColor: 'var(--green)', flexShrink: 0 }} />
              Utan lagersaldo — bara ett pris, inget antal (t.ex. kaffe)
            </label>
          )}
          {untracked ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: '#fbf7ee', borderRadius: 12, padding: '12px 16px', fontSize: 13.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
              <Icon name="tag" size={18} color="var(--green)" />
              {groupUntracked ? 'Gruppen saknar lagersaldo — varan' : 'Varan'} har bara ett pris, inget antal eller inköpspris. Hela priset räknas som vinst.
            </div>
          ) : (<>
            <div className="form-cols" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              {!hasRecipe && (
                <div className="field"><label style={{ whiteSpace: 'nowrap' }}>Inköp (kr)</label>
                  <input className="input" type="number" inputMode="numeric" value={f.cost} onChange={e => set('cost', e.target.value)} placeholder="8" /></div>
              )}
              <div className="field"><label style={{ whiteSpace: 'nowrap' }}>{hasRecipe ? 'Antal (från recept)' : 'Antal'}</label>
                <input className="input" type="number" inputMode="numeric" value={f.stock} onChange={e => set('stock', e.target.value)}
                  placeholder="40" disabled={hasRecipe} style={hasRecipe ? { opacity: .5 } : {}} /></div>
              {!hasRecipe && (
                <div className="field"><label style={{ whiteSpace: 'nowrap' }} title="Antalet på snabbknappen i lagerlistan">Påfyllnadsknapp</label>
                  <input className="input" type="number" inputMode="numeric" value={f.restockQty} onChange={e => set('restockQty', e.target.value)} placeholder="12" /></div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', background: '#fbf7ee', borderRadius: 12, padding: '12px 16px' }}>
              <Icon name="tag" size={18} color="var(--green)" />
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                {hasRecipe && <>Inköp från receptet: <b style={{ color: 'var(--green-deep)' }}>{recipeCost} kr</b> · </>}
                Marginal: <b style={{ color: 'var(--green-deep)' }}>{margin} kr</b> per vara{effPrice > 0 ? ` (${Math.round(margin / effPrice * 100)}%)` : ''}
              </span>
            </div>
          </>)}

          {/* Kassans specialfall: grillräknaren och sektionen Domare & ledare. */}
          <div className="field">
            <label>I kassan</label>
            {[
              ['meal', '🍔', 'Hamburgarmål', 'varje sålt plockar en burgare från grillen'],
              ['freeForLeaders', '☕', 'Gratis till ledare', 'visas under Domare & ledare, lämnas ut till 0 kr'],
              ['freeForReferees', '🧑‍⚖️', 'Gratis till domare', 'visas under Domare & ledare, lämnas ut till 0 kr'],
            ].map(([key, emoji, title, hint]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '7px 0', fontSize: 14, fontWeight: 700, color: f[key] ? 'var(--green-deep)' : 'var(--ink-soft)' }}>
                <input type="checkbox" checked={!!f[key]} onChange={e => set(key, e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: 'var(--green)', flexShrink: 0 }} />
                <span style={{ fontSize: 17 }}>{emoji}</span>
                <span>{title} <span style={{ fontWeight: 600, color: 'var(--muted)' }}>— {hint}</span></span>
              </label>
            ))}
            {(f.freeForLeaders || f.freeForReferees) && (
              <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600, marginTop: 2 }}>
                Varan visas då bara under Domare &amp; ledare, inte i det vanliga sortimentet.
              </div>
            )}
          </div>

          {/* Tillval som ingår: drickan i ett Hamburgarmål eller till domarens korv. */}
          <div className="field">
            <label>Tillval som ingår i priset</label>
            <select className="select" value={f.choiceGroupId || ''} onChange={e => set('choiceGroupId', e.target.value || null)}>
              <option value="">Inget tillval</option>
              {state.groups.filter(g => g.id !== f.groupId).map(g => <option key={g.id} value={g.id}>Kunden väljer en vara ur {g.name}</option>)}
            </select>
            {f.choiceGroupId && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fbf7ee', borderRadius: 12, padding: '10px 14px', fontSize: 13, fontWeight: 600, color: 'var(--ink-soft)', lineHeight: 1.45 }}>
                <Icon name="info" size={17} color="var(--green)" />
                <span>Kassan frågar vilken som togs — en per såld vara — och drar den från lagret till 0 kr. Säljs 3 mål väljs 3 drickor.</span>
              </div>
            )}
          </div>
        </div>

        <div className="field"><label>Bild / symbol (ej nödvändigt)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <div className="center" style={{ width: 60, height: 60, borderRadius: 16, background: '#fbf6ec', fontSize: 30 }}>{f.emoji}</div>
            <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Välj en symbol som visas i kassan</span>
          </div>
          <EmojiPick value={f.emoji} onChange={v => set('emoji', v)} />
        </div>

        {/* recipe builder — full width under the two columns */}
        {!untracked && (
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Recept — lagervaror som förbrukas per såld vara</label>
            {recipe.map(([iid, q], i) => {
              const ing = state.ingredients.find(x => x.id === iid);
              return (
                <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 8 }}>
                  <select className="select" style={{ flex: 1, minWidth: 0, padding: '10px 12px' }} value={iid}
                    onChange={e => set('recipe', recipe.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))}>
                    {state.ingredients.map(x => <option key={x.id} value={x.id}>{x.emoji} {x.name}</option>)}
                    {!ing && <option value={iid}>(borttagen lagervara)</option>}
                  </select>
                  <Stepper value={q} min={1} onChange={v => set('recipe', recipe.map((r, j) => (j === i ? [r[0], Math.max(1, v)] : r)))} />
                  <button className="btn btn-icon btn-ghost" style={{ width: 38, height: 38 }}
                    onClick={() => set('recipe', recipe.filter((_, j) => j !== i))}><Icon name="trash" size={15} /></button>
                </div>
              );
            })}
            {state.ingredients.length > 0
              ? <button className="btn btn-ghost btn-sm" onClick={() => set('recipe', [...recipe, [state.ingredients[0].id, 1]])}><Icon name="plus" size={15} /> Lägg till lagervara</button>
              : <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>Inga lagervaror ännu — skapa dem under Inventering.</div>}
            {hasRecipe && <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600, marginTop: 6 }}>Lagret beräknas från lagervarorna och dras av automatiskt vid försäljning. Inköpspriset följer lagervarornas inköp.</div>}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ---------------- GROUP FORM ---------------- */
function GroupForm({ group, onClose }) {
  const [f, setF] = useState(group
    ? { ...group, discount: group.discount ? String(group.discount) : '' }
    : { name: '', color: '#3f8197', emoji: '🍽️', untracked: false, discount: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const colors = ['#c26a45', '#3f8197', '#a65a78', '#b58a30', '#2f7d49', '#6a6f9b', '#c0473a', '#557a52'];
  const discount = Math.min(100, Math.max(0, Math.round(+f.discount || 0)));

  async function save() {
    if (busy) return;
    setBusy(true);
    const patch = { name: f.name.trim(), color: f.color, emoji: f.emoji, untracked: !!f.untracked, discount };
    try {
      if (group) await actions.updateGroup(group.id, patch);
      else await actions.addGroup(patch);
      onClose();
    } catch {
      setBusy(false);
    }
  }

  return (
    <Modal title={group ? 'Ändra grupp' : 'Ny grupp'} onClose={onClose}
      footer={<>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" onClick={onClose}>Avbryt</button>
        <button className="btn btn-primary" disabled={!f.name.trim() || busy} onClick={save}><Icon name="check" size={19} /> Spara</button>
      </>}>
      <div className="field" style={{ marginBottom: 18 }}><label>Namn på grupp</label>
        <input className="input" value={f.name} autoFocus onChange={e => set('name', e.target.value)} placeholder="T.ex. Varm mat" /></div>
      <div className="field" style={{ marginBottom: 18 }}><label>Färg</label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {colors.map(c => (
            <button key={c} onClick={() => set('color', c)} className="center"
              style={{ width: 42, height: 42, borderRadius: 12, background: c, cursor: 'pointer', border: '3px solid ' + (f.color === c ? 'var(--ink)' : 'transparent') }}>
              {f.color === c && <Icon name="check" size={20} color="#fff" />}
            </button>
          ))}
        </div></div>
      <div className="field" style={{ marginBottom: 18 }}><label>Symbol</label><EmojiPick value={f.emoji} onChange={v => set('emoji', v)} /></div>
      {/* Tillfällig rabatt på hela gruppen — ligger kvar tills den nollas. */}
      <div className="field" style={{ marginBottom: 18 }}>
        <label>Rabatt på hela gruppen (%) — tillfälligt</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="input" type="number" inputMode="numeric" min={0} max={100} style={{ width: 120 }}
            value={f.discount} onChange={e => set('discount', e.target.value)} placeholder="0" />
          {[25, 50].map(n => (
            <button key={n} type="button" className={'btn btn-sm ' + (discount === n ? 'btn-orange' : 'btn-ghost')} onClick={() => set('discount', String(n))}>−{n} %</button>
          ))}
          {discount > 0 && <button type="button" className="btn btn-sm btn-ghost" onClick={() => set('discount', '')}>Ingen rabatt</button>}
        </div>
        <div style={{ fontSize: 12.5, color: discount > 0 ? 'var(--orange-deep)' : 'var(--muted)', fontWeight: 600, lineHeight: 1.45 }}>
          {discount > 0
            ? <>Alla varor i gruppen säljs för {100 - discount} % av ordinarie pris tills rabatten tas bort. Trumfar eventuella extrapriser.</>
            : <>T.ex. 50 % på all glass en varm helg. Räknas på ordinarie pris och trumfar extrapriser.</>}
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 11, cursor: 'pointer', background: '#fbf7ee', borderRadius: 12, padding: '13px 15px' }}>
        <input type="checkbox" checked={!!f.untracked} onChange={e => set('untracked', e.target.checked)}
          style={{ width: 19, height: 19, accentColor: 'var(--green)', marginTop: 1, flexShrink: 0 }} />
        <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.45 }}>
          Utan lagersaldo
          <span style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--ink-soft)' }}>
            Varorna har bara ett pris — inget antal och inget inköpspris. Passar t.ex. Hembakat.
          </span>
        </span>
      </label>
    </Modal>
  );
}

/* ---------------- KIOSKSCHEMA ---------------- */
export function Schema() {
  const state = useStore();
  const curWeek = isoWeek(new Date());
  const year = new Date().getFullYear();
  const rows = [...state.schedule].sort((a, b) => a.year - b.year || a.week - b.week);
  const [newWeek, setNewWeek] = useState(curWeek);

  return (
    <div className="scroll" style={{ height: '100%' }}>
      <PageHead title="Kioskschema" sub="Lägg in vilket lag som har hand om kiosken vecka för vecka. Bocka i grillning de veckor det grillas och välj vilka dagar — kiosken visar grilldelarna bara då." />
      <div className="admin-body" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 'min(760px,100%)' }}>
          <div className="card" style={{ padding: '16px 20px', marginBottom: 18, display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 14 }}>
            <div className="field" style={{ width: 120 }}><label>Vecka</label>
              <input className="input" type="number" value={newWeek} onChange={e => setNewWeek(+e.target.value)} /></div>
            <div className="m-hide" style={{ flex: 1, color: 'var(--muted)', fontSize: 14, fontWeight: 600, paddingBottom: 13 }}>Välj lag i listan när veckan lagts till →</div>
            <button className="btn btn-primary" onClick={() => actions.setSchedule(newWeek, year, state.teams[0] || '')}>
              <Icon name="plus" size={18} /> Lägg till vecka
            </button>
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            {rows.map((r, i) => {
              const isNow = r.week === curWeek && r.year === year;
              const days = r.grillDays || [];
              const today = isoDay();
              const toggleDay = d => actions.setSchedule(r.week, r.year, r.team, true,
                days.includes(d) ? days.filter(x => x !== d) : [...days, d]);
              return (
                <div key={r.year + '-' + r.week} style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', flexWrap: 'wrap',
                  borderBottom: i < rows.length - 1 ? '1px solid var(--line-soft)' : 'none',
                  background: isNow ? '#eaf2ec' : 'transparent',
                }}>
                  <div className="center" style={{
                    width: 58, height: 50, borderRadius: 13, flexShrink: 0, flexDirection: 'column',
                    background: isNow ? 'var(--green)' : '#f0e8d6', color: isNow ? '#fff' : 'var(--green-deep)',
                  }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, opacity: .85, marginTop: 2 }}>VECKA</span>
                    <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 20, lineHeight: 1 }}>{r.week}</span>
                  </div>
                  {isNow && <span className="pill" style={{ background: 'var(--green)', color: '#fff' }}>Denna vecka</span>}
                  <div style={{ flex: 1 }} />
                  <label title="Kiosken visar Grillen-fliken och grillräknaren på Hamburgarmålet bara när det grillas"
                    style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', color: r.grill ? 'var(--orange-deep)' : 'var(--muted)' }}>
                    <input type="checkbox" checked={!!r.grill} style={{ width: 18, height: 18, accentColor: 'var(--orange)' }}
                      onChange={e => actions.setSchedule(r.week, r.year, r.team, e.target.checked, days)} />
                    🍔 Grillning
                  </label>
                  <select className="select sched-team" value={r.team} onChange={e => actions.setSchedule(r.week, r.year, e.target.value, !!r.grill, days)}>
                    {state.teams.map(t => <option key={t} value={t}>{t}</option>)}
                    {!state.teams.includes(r.team) && <option value={r.team}>{r.team}</option>}
                  </select>
                  <button className="btn btn-icon btn-ghost" onClick={() => actions.removeSchedule(r.week, r.year)}><Icon name="trash" size={17} /></button>

                  {/* Vilka dagar det grillas. Inga valda = hela veckan, så gamla rader fungerar som förut. */}
                  {r.grill && (
                    <div className="sched-days" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginRight: 4 }}>Grilldagar</span>
                      {DAY_NAMES.map((name, idx) => {
                        const d = idx + 1;
                        const on = days.includes(d);
                        const isToday = isNow && d === today;
                        return (
                          <button key={d} type="button" onClick={() => toggleDay(d)}
                            title={isToday ? 'Idag' : undefined}
                            style={{
                              minWidth: 46, height: 36, padding: '0 10px', borderRadius: 999, fontWeight: 800, fontSize: 13.5, cursor: 'pointer',
                              border: '1.5px solid ' + (on ? 'var(--orange)' : isToday ? 'var(--green)' : 'var(--line)'),
                              background: on ? 'var(--orange)' : '#fff', color: on ? '#fff' : 'var(--ink-soft)',
                            }}>
                            {name}{isToday ? ' ·' : ''}
                          </button>
                        );
                      })}
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: days.length ? 'var(--orange-deep)' : 'var(--muted)' }}>
                        {days.length ? 'Grillen visas bara dessa dagar' : 'Inga dagar valda — grillen visas hela veckan'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
            {rows.length === 0 && <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontWeight: 600 }}>Inget schema inlagt än.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- FÖRSÄLJNING ---------------- */
/* Hur många köp listan visar innan den kapas — en hel säsong i en rad vore
   ogenomtränglig, och de senaste är de som brukar behöva granskas. */
const SALES_SHOWN = 120;

export function SalesLog() {
  const state = useStore();
  const [period, setPeriodRaw] = useState('week');
  const [grouping, setGrouping] = useState('day');
  const [adding, setAdding] = useState(false);
  /* Bläddra bakåt: ref är en dag inne i perioden som visas. Byter man
     periodlängd hoppar vyn tillbaka till nu, annars blir det lätt förvirrande. */
  const [ref, setRef] = useState(() => new Date());
  const setPeriod = p => { setPeriodRaw(p); setRef(new Date()); };
  const [from, to] = rangeFor(period, ref);
  const isCurrent = to > Date.now();
  const sales = state.sales.filter(s => s.ts >= from && s.ts < to).sort((a, b) => b.ts - a.ts);
  const sum = sales.reduce((a, s) => a + s.total, 0);
  const profit = sales.reduce((a, s) => a + s.profit, 0);

  /* Dagsgrupper hör hemma i veckovyn: en kioskvecka är några matchdagar, och
     då är dagssumman den siffra man faktiskt jämför. Över en månad eller ett
     år blir det för många rubriker för att hjälpa. */
  const canGroup = period === 'week';
  const byDay = canGroup && grouping === 'day';
  const shown = sales.slice(0, SALES_SHOWN);
  const days = byDay ? salesByDay(shown) : [];

  return (
    <div className="scroll" style={{ height: '100%' }}>
      <PageHead title="Försäljning" sub="Alla registrerade köp. Strulade appen vid disken kan du lägga till köpet i efterhand."
        right={<div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canGroup && (
            <Segmented value={grouping} onChange={setGrouping}
              options={[['day', 'Per dag'], ['flat', 'Alla köp']]} />
          )}
          <PeriodToggle value={period} onChange={setPeriod} withDay />
          <button className="btn btn-primary" onClick={() => setAdding(true)}><Icon name="plus" size={18} /> Lägg till köp</button>
        </div>} />
      <div className="admin-body" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 'min(820px,100%)' }}>
          {/* bläddra i tiden */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <button className="btn btn-ghost btn-icon" title="Föregående" onClick={() => setRef(shiftPeriod(period, ref, -1))}><Icon name="back" size={18} /></button>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 20, color: 'var(--green-deep)' }}>{periodLabel(period, ref)}</div>
              {!isCurrent && (
                <button className="btn btn-sm btn-soft" style={{ marginTop: 4 }} onClick={() => setRef(new Date())}>Tillbaka till {period === 'day' ? 'idag' : period === 'week' ? 'denna vecka' : period === 'month' ? 'denna månad' : 'i år'}</button>
              )}
            </div>
            <button className="btn btn-ghost btn-icon" title="Nästa" disabled={isCurrent} style={isCurrent ? { opacity: .4 } : {}}
              onClick={() => setRef(shiftPeriod(period, ref, 1))}><Icon name="arrow" size={18} /></button>
          </div>
          <div className="grid-kpi" style={{ marginBottom: 18 }}>
            <Stat label="Antal köp" value={sales.length} icon="receipt" accent="#3f8197" />
            <Stat label="Intäkter" value={kr(sum)} icon="money" accent="var(--green)" />
            <Stat label="Vinst" value={kr(profit)} icon="chart" accent="var(--orange)" tone="var(--orange-deep)" />
          </div>
          <div className="card" style={{ overflow: 'hidden' }}>
            {sales.length === 0 && <div style={{ padding: '28px', textAlign: 'center', color: 'var(--muted)', fontWeight: 600 }}>Inga köp registrerade {period === 'day' ? 'den här dagen' : 'i perioden'}.</div>}
            {!byDay && shown.map(s => <SaleRow key={s.id} sale={s} showDate={period !== 'day'} />)}
            {byDay && days.map((day, i) => (
              <div key={day.ts}>
                <DayHeader day={day} first={i === 0} />
                {day.rows.map(s => <SaleRow key={s.id} sale={s} />)}
              </div>
            ))}
            {sales.length > SALES_SHOWN && (
              <div style={{ padding: '12px 20px', textAlign: 'center', color: 'var(--muted)', fontWeight: 600, fontSize: 13 }}>
                Visar de {SALES_SHOWN} senaste av {sales.length} köp i perioden.
              </div>
            )}
          </div>
        </div>
      </div>
      {adding && <AddSaleForm day={period === 'day' ? ref : null} onClose={() => setAdding(false)} />}
    </div>
  );
}

/* Dagsrubrik i veckovyn: vad dagen drog in, inte bara vilka köp som gjordes. */
function DayHeader({ day, first }) {
  const d = new Date(day.ts);
  const label = d.toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
  const today = new Date().toDateString() === d.toDateString();
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 12,
      padding: '12px 20px', background: '#f4ecdc',
      /* Första rubriken sitter mot kortets egen kant — ingen linje ovanför. */
      borderTop: first ? 'none' : '1.5px solid var(--line)',
      borderBottom: '1.5px solid var(--line-soft)',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, flex: 1, minWidth: 0 }}>
        <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, color: 'var(--green-deep)' }}>
          {label.charAt(0).toUpperCase() + label.slice(1)}
        </span>
        {today && <span className="pill tag-good" style={{ fontSize: 11 }}>idag</span>}
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
          {day.rows.length} köp
        </span>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, color: 'var(--green-deep)' }}>{kr(day.total)}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>vinst {kr(day.profit)}</div>
      </div>
      {/* Lika brett som radernas papperskorg, så summan står rakt ovanför beloppen. */}
      <div style={{ width: 38, flexShrink: 0 }} />
    </div>
  );
}

/* Ett köp i listan. Datumet står i dagsrubriken när köpen är grupperade, så
   raden nöjer sig med klockslaget då. */
function SaleRow({ sale: s, showDate }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: '1px solid var(--line-soft)' }}>
      <div style={{ width: 64, flexShrink: 0, textAlign: 'center', color: 'var(--muted)', lineHeight: 1.25 }}>
        {showDate && <div style={{ fontWeight: 800, fontSize: 14.5, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{new Date(s.ts).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}</div>}
        <div style={{ fontSize: showDate ? 12.5 : 14.5, fontWeight: showDate ? 600 : 800, color: showDate ? 'var(--muted)' : 'var(--ink)', whiteSpace: 'nowrap' }}>
          {new Date(s.ts).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <PaymentTag payment={s.payment} />
          <SaleItems items={s.items} />
        </div>
        {(s.by || s.manual) && (
          <div style={{ fontSize: 12, fontWeight: 700, color: s.manual ? 'var(--orange-deep)' : 'var(--muted)', marginTop: 5 }}>
            {s.manual ? '🛠 Tillagd i efterhand av ' + (s.by || 'Admin') : 'Inslaget av ' + s.by}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, color: 'var(--green-deep)' }}>{s.total} kr</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>vinst {Math.round(s.profit)} kr</div>
      </div>
      <button className="btn btn-icon btn-ghost" style={{ width: 38, height: 38, flexShrink: 0 }} title="Ta bort köpet — lagret återförs" onClick={() => {
        askConfirm('Köpet tas bort ur försäljningen och varorna läggs tillbaka i lagret.',
          { title: 'Ta bort köp?', danger: true, confirmLabel: 'Ta bort' })
          .then(ok => ok && actions.deleteSale(s));
      }}><Icon name="trash" size={15} /></button>
    </div>
  );
}

/* Admin lägger till ett köp i efterhand — t.ex. när appen strulade vid disken.
   Lagret dras som vid ett vanligt köp och raden märks som tillagd av admin. */
/* `day`: dagen som visas i Dag-vyn — köpet läggs då på den dagen, med
   klockslaget just nu som startvärde. */
function AddSaleForm({ onClose, day }) {
  const state = useStore();
  const [rows, setRows] = useState([{ id: state.products[0]?.id || '', qty: 1 }]);
  const [when, setWhen] = useState(() => {
    const now = new Date();
    const d = day ? new Date(day) : now;
    d.setHours(now.getHours(), now.getMinutes(), 0, 0);
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  });
  const [busy, setBusy] = useState(false);

  const lines = rows.filter(r => r.id && r.qty > 0);
  const total = lines.reduce((a, r) => {
    const p = state.products.find(x => x.id === r.id);
    return a + (p ? priceFor(p, state) * r.qty : 0);
  }, 0);

  /* Slut i lager hindrar inte admin: köpet kan ha slagits in fel i kiosken
     men intäkten ska ändå med. Lagret dras till noll och rättas vid nästa
     inventering. */
  const confirmShort = intro => askConfirm(
    <>
      {intro}
      <br /><br />
      Lägg till köpet ändå? Intäkten kommer med och lagret sätts till 0 — gör en inventering efteråt så att siffrorna blir rätt.
    </>,
    { title: 'Slut i lager', confirmLabel: 'Lägg till ändå' });

  async function save() {
    if (!lines.length || busy) return;
    const cart = lines.map(r => ({ id: r.id, qty: r.qty }));
    let allowShort = false;
    const short = shortagesFor(state, cart);
    if (short.length) {
      const intro = <>Enligt lagret räcker inte {short.map((s, i) => (
        <span key={i}>{i > 0 && (i === short.length - 1 ? ' och ' : ', ')}<b>{s.name}</b> ({s.stock} kvar, köpet behöver {s.need})</span>
      ))}.</>;
      if (!await confirmShort(intro)) return;
      allowShort = true;
    }
    setBusy(true);
    const ts = new Date(when).getTime() || Date.now();
    try {
      await actions.addManualSale(cart, ts, { allowShort });
      onClose();
    } catch (err) {
      /* Lagret hann ändras sedan sidan laddades — fråga med serverns besked. */
      if (!allowShort && err && err.status === 409) {
        dismissError();
        const ok = await confirmShort(err.message);
        if (ok) {
          try {
            await actions.addManualSale(cart, ts, { allowShort: true });
            onClose();
            return;
          } catch { /* reported by the store */ }
        }
      }
      setBusy(false);
    }
  }

  return (
    <Modal title="Lägg till köp i efterhand" onClose={onClose} closeOnBackdrop={false}
      footer={<>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" onClick={onClose}>Avbryt</button>
        <button className="btn btn-primary" disabled={!lines.length || busy} onClick={save}>
          {busy ? <span className="spinner" /> : <Icon name="check" size={19} />} Spara köp · {total} kr
        </button>
      </>}>
      <div className="field" style={{ marginBottom: 16 }}>
        <label>När gjordes köpet?</label>
        <input className="input" type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} />
      </div>
      <div className="field">
        <label>Varor</label>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 9, alignItems: 'center', marginBottom: 8 }}>
            <select className="select" style={{ flex: 1, minWidth: 0, padding: '10px 12px' }} value={r.id}
              onChange={e => setRows(rows.map((x, j) => (j === i ? { ...x, id: e.target.value } : x)))}>
              {state.products.map(p => <option key={p.id} value={p.id}>{p.emoji} {p.name} · {priceFor(p, state)} kr</option>)}
            </select>
            <Stepper small value={r.qty} min={1} onChange={v => setRows(rows.map((x, j) => (j === i ? { ...x, qty: Math.max(1, v) } : x)))} />
            {rows.length > 1 && (
              <button className="btn btn-icon btn-ghost" style={{ width: 38, height: 38 }}
                onClick={() => setRows(rows.filter((_, j) => j !== i))}><Icon name="trash" size={15} /></button>
            )}
          </div>
        ))}
        <button className="btn btn-ghost btn-sm" onClick={() => setRows([...rows, { id: state.products[0]?.id || '', qty: 1 }])}>
          <Icon name="plus" size={15} /> Lägg till vara
        </button>
      </div>
      <div style={{ marginTop: 14, fontSize: 13, color: 'var(--muted)', fontWeight: 600, lineHeight: 1.5 }}>
        Lagret dras av som vid ett vanligt köp, och raden märks med <b>🛠 Tillagd i efterhand av Admin</b>.
      </div>
    </Modal>
  );
}

/* ---------------- INSTÄLLNINGAR ---------------- */
export function Settings() {
  const state = useStore();
  const [user, setUser] = useState(state.adminUser || '');
  const [userErr, setUserErr] = useState('');
  const [pass, setPass] = useState('');
  const [code, setCode] = useState('');
  const [demoPin, setDemoPin] = useState('');
  const [low, setLow] = useState(state.settings.lowStock);
  const [saved, setSaved] = useState(false);
  const [newTeam, setNewTeam] = useState('');
  /* Kansliets kontaktuppgifter — visas bakom knappen "Kansliet" i kiosken. */
  const [contact, setContact] = useState({
    contactName: state.settings.contactName || '',
    contactPhone: state.settings.contactPhone || '',
    contactEmail: state.settings.contactEmail || '',
    contactNote: state.settings.contactNote || '',
  });
  const setC = (k, v) => setContact(s => ({ ...s, [k]: v }));

  const [pinErr, setPinErr] = useState('');

  async function save() {
    const patch = { lowStock: +low || 5 };
    for (const [k, v] of Object.entries(contact)) patch[k] = v.trim();
    const u = user.trim().toLowerCase();
    if (u && u !== state.adminUser) {
      if (!/^[a-z0-9._@-]{3,40}$/.test(u)) {
        setUserErr('Användarnamnet ska vara 3–40 tecken: bokstäver a–z, siffror och . _ - @.');
        return;
      }
      patch.adminUser = u;
    }
    if (pass.trim()) patch.adminPass = pass.trim();
    if (code.trim()) patch.kioskCode = code.trim();
    /* Kiosken knappar in demokoden på ett sifferblock — fyra siffror, inget annat. */
    if (demoPin.trim()) {
      if (!/^\d{4}$/.test(demoPin.trim())) {
        setPinErr('Demokoden ska vara fyra siffror.');
        return;
      }
      patch.demoPin = demoPin.trim();
    }
    setPinErr('');
    try {
      await actions.updateSettings(patch);
      setPass('');
      setCode('');
      setDemoPin('');
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch { /* reported by the store */ }
  }

  return (
    <div className="scroll" style={{ height: '100%' }}>
      <PageHead title="Inställningar" />
      <div className="admin-body" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 'min(560px,100%)', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 16 }}>Kiosk & lager</div>
            <div className="field" style={{ marginBottom: 16 }}><label>Admin-användarnamn</label>
              <input className="input" autoComplete="off" autoCapitalize="none" spellCheck={false} value={user}
                onChange={e => { setUser(e.target.value); setUserErr(''); }} placeholder="t.ex. kansliet" />
              {userErr && <div style={{ fontSize: 13, color: 'var(--bad)', fontWeight: 700 }}>{userErr}</div>}
              <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>
                Krävs tillsammans med lösenordet vid inloggning. 3–40 tecken: a–z, siffror och . _ - @
              </div></div>
            <div className="field" style={{ marginBottom: 16 }}><label>Nytt admin-lösenord</label>
              <input className="input" type="password" autoComplete="new-password" value={pass}
                onChange={e => setPass(e.target.value)} placeholder="Lämna tomt för att behålla nuvarande" /></div>
            <div className="field" style={{ marginBottom: 16 }}><label>Ny kiosk-kod (för inloggning i kiosken)</label>
              <input className="input" type="password" autoComplete="new-password" value={code}
                onChange={e => setCode(e.target.value)} placeholder="Lämna tomt för att behålla nuvarande" /></div>
            <div className="field" style={{ marginBottom: 18 }}><label>Varna för "lågt lager" när antal är ≤</label>
              <input className="input" type="number" value={low} onChange={e => setLow(e.target.value)} style={{ width: 120 }} /></div>
            <button className="btn btn-primary" onClick={save}><Icon name="check" size={19} /> {saved ? 'Sparat!' : 'Spara inställningar'}</button>
          </div>

          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 8 }}>Kansliet i kiosken</div>
            <p style={{ margin: '0 0 14px', color: 'var(--ink-soft)', fontSize: 14.5, lineHeight: 1.5 }}>
              Fyll i hur laget når kansliet om något strular under passet. Kiosken får då knappen
              <b> Kansliet</b> i topplisten, med numret och adressen klickbara. Lämnas allt tomt visas ingen knapp.
            </p>
            <div className="grid-fill" style={{ marginBottom: 12 }}>
              <div className="field"><label>Namn / vem svarar</label>
                <input className="input" value={contact.contactName} onChange={e => setC('contactName', e.target.value)} placeholder="T.ex. Kansliet, Anna" /></div>
              <div className="field"><label>Telefon</label>
                <input className="input" type="tel" inputMode="tel" value={contact.contactPhone} onChange={e => setC('contactPhone', e.target.value)} placeholder="070-123 45 67" /></div>
              <div className="field"><label>E-post</label>
                <input className="input" type="email" inputMode="email" value={contact.contactEmail} onChange={e => setC('contactEmail', e.target.value)} placeholder="kansliet@sandaredsif.se" /></div>
            </div>
            <div className="field" style={{ marginBottom: 14 }}><label>Bra att veta (valfritt)</label>
              <textarea className="textarea" rows={2} value={contact.contactNote} onChange={e => setC('contactNote', e.target.value)}
                placeholder="T.ex. Ring hellre än mejla på matchdagar. Nyckel till förrådet hänger i kansliet." /></div>
            <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>
              Sparas när du trycker <b>Spara inställningar</b> ovan.
            </div>
          </div>

          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 8 }}>Demoläge</div>
            <p style={{ margin: '0 0 14px', color: 'var(--ink-soft)', fontSize: 14.5, lineHeight: 1.5 }}>
              Med en demokod satt dyker knappen <b>Demo</b> upp i kiosken. Laget knappar in
              koden och kan sedan öva på allt — köp, mål, noteringar och svinn — utan att
              något sparas. Demot avslutas utan kod. {state.demoPinSet
                ? 'En kod finns redan; skriv en ny för att byta.'
                : 'Ingen kod är satt än, så knappen syns inte i kiosken.'}
            </p>
            <div className="field" style={{ marginBottom: 14 }}><label>Demokod (fyra siffror)</label>
              <input className="input" type="password" inputMode="numeric" autoComplete="new-password"
                value={demoPin} maxLength={4} style={{ width: 160 }}
                onChange={e => { setDemoPin(e.target.value.replace(/\D/g, '')); setPinErr(''); }}
                placeholder={state.demoPinSet ? '••••' : '0000'} /></div>
            {pinErr && (
              <div style={{ background: '#fbeae7', color: 'var(--bad)', borderRadius: 10, padding: '10px 13px', fontSize: 13.5, fontWeight: 700, marginBottom: 14 }}>{pinErr}</div>
            )}
            <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>
              Koden sparas tillsammans med de andra lösenorden när du trycker <b>Spara inställningar</b> ovan.
            </div>
          </div>

          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 8 }}>Lag</div>
            <p style={{ margin: '0 0 14px', color: 'var(--ink-soft)', fontSize: 14.5, lineHeight: 1.5 }}>Lagen som går att välja i kioskschemats rullista.</p>
            {state.teams.map(t => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--line-soft)' }}>
                <span style={{ flex: 1, fontWeight: 700, fontSize: 15 }}>{t}</span>
                <button className="btn btn-icon btn-ghost" onClick={() => {
                  askConfirm(`„${t}” tas bort ur rullistan. Veckor som redan har laget behåller det.`,
                    { title: 'Ta bort lag?', danger: true, confirmLabel: 'Ta bort' })
                    .then(ok => ok && actions.removeTeam(t));
                }}><Icon name="trash" size={16} /></button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <input className="input" value={newTeam} onChange={e => setNewTeam(e.target.value)} placeholder="T.ex. Lag P09"
                onKeyDown={e => { if (e.key === 'Enter' && newTeam.trim()) { actions.addTeam(newTeam); setNewTeam(''); } }} />
              <button className="btn btn-primary" disabled={!newTeam.trim()} onClick={() => { actions.addTeam(newTeam); setNewTeam(''); }}>
                <Icon name="plus" size={18} /> Lägg till
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ---------------- KIOSKGUIDEN ---------------- */
export function GuideEditor() {
  const state = useStore();
  const [g, setG] = useState(() => structuredClone(state.guide));
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setG(s => ({ ...s, [k]: v }));

  async function save() {
    try {
      await actions.updateGuide(structuredClone(g));
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch { /* reported by the store */ }
  }

  return (
    <div className="scroll" style={{ height: '100%' }}>
      <PageHead title="Kioskguiden" sub={'Texterna som visas under fliken „Så funkar kiosken” i kioskläget.'}
        right={<button className="btn btn-primary" onClick={save}><Icon name="check" size={19} /> {saved ? 'Sparat!' : 'Spara guiden'}</button>} />
      <div className="admin-body" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 'min(760px,100%)', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Välkomsttext</div>
            <textarea className="textarea" rows={2} value={g.intro} onChange={e => set('intro', e.target.value)} />
            <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600, marginTop: 8 }}>Veckans lag läggs till automatiskt efter texten.</div>
          </div>

          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Stegen (1–4)</div>
            {g.steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-start' }}>
                <div className="center" style={{ width: 36, height: 36, borderRadius: 11, background: 'var(--green)', color: '#fff', fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, flexShrink: 0, marginTop: 4 }}>{i + 1}</div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input className="input" value={s.t} placeholder="Rubrik" onChange={e => set('steps', g.steps.map((x, j) => (j === i ? { ...x, t: e.target.value } : x)))} />
                  <textarea className="textarea" rows={2} value={s.d} placeholder="Beskrivning" onChange={e => set('steps', g.steps.map((x, j) => (j === i ? { ...x, d: e.target.value } : x)))} />
                </div>
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Var finns sakerna?</div>
            {g.where.map(([a, b], i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
                <input className="input" value={a} placeholder="Vad" onChange={e => set('where', g.where.map((x, j) => (j === i ? [e.target.value, x[1]] : x)))} />
                <input className="input" value={b} placeholder="Var" onChange={e => set('where', g.where.map((x, j) => (j === i ? [x[0], e.target.value] : x)))} />
                <button className="btn btn-icon btn-ghost" onClick={() => set('where', g.where.filter((_, j) => j !== i))}><Icon name="trash" size={16} /></button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={() => set('where', [...g.where, ['', '']])}><Icon name="plus" size={16} /> Lägg till rad</button>
          </div>

          <div className="card" style={{ padding: '22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Bra att veta</div>
            {g.tips.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
                <input className="input" value={t} onChange={e => set('tips', g.tips.map((x, j) => (j === i ? e.target.value : x)))} />
                <button className="btn btn-icon btn-ghost" onClick={() => set('tips', g.tips.filter((_, j) => j !== i))}><Icon name="trash" size={16} /></button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={() => set('tips', [...g.tips, ''])}><Icon name="plus" size={16} /> Lägg till rad</button>
          </div>
        </div>
      </div>
    </div>
  );
}
